import React from "react";

import { useTranslation } from "react-i18next";

import Button from "@/components/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/auth";
import {
  useCommunicationPreferences,
  useUpdateCommunicationPreferences,
} from "@/queries/api/communicationPrefsApi";
import { notify } from "@/utils";

function maskPhone(phone?: string): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 4) return null;
  return `•••• ${digits.slice(-4)}`;
}

export function CommunicationPreferencesPanel() {
  const { t } = useTranslation("settings");
  const auth = useAuth();
  const prefsQuery = useCommunicationPreferences();
  const updatePrefs = useUpdateCommunicationPreferences();

  const [smsUpdates, setSmsUpdates] = React.useState(false);
  const [emailUpdates, setEmailUpdates] = React.useState(true);

  // Hydrate local state once the saved preferences load.
  React.useEffect(() => {
    if (prefsQuery.data) {
      setSmsUpdates(prefsQuery.data.sms_updates);
      setEmailUpdates(prefsQuery.data.email_updates);
    }
  }, [prefsQuery.data]);

  const maskedPhone = maskPhone(auth.user?.phone);

  const handleSave = async () => {
    try {
      await updatePrefs.mutateAsync({
        sms_updates: smsUpdates,
        email_updates: emailUpdates,
      });
      notify.success(t("communication.saved"));
    } catch (e) {
      notify.error(
        e instanceof Error ? e.message : t("communication.saveError"),
      );
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <h3 className="text-xl font-semibold text-garnet mb-2">
          {t("communication.title")}
        </h3>
        <p className="text-sm text-garnet/80">
          {t("communication.description")}
        </p>
      </div>

      {/* Transactional — always on */}
      <div className="flex flex-row gap-3 items-start p-4 rounded-lg ring-1 ring-garnet/20 bg-fog/40">
        <Checkbox
          checked
          disabled
          className="size-5 mt-0.5 shrink-0 border-2 border-garnet/40 data-[state=checked]:bg-garnet/50"
        />
        <div className="flex-1">
          <p className="body-small font-semibold text-garnet">
            {t("communication.transactionalTitle")}
          </p>
          <p className="text-xs text-garnet/70 mt-1">
            {t("communication.transactionalDesc")}
          </p>
        </div>
      </div>

      {/* SMS updates — optional */}
      <ToggleRow
        id="comm-sms-updates"
        checked={smsUpdates}
        onChange={setSmsUpdates}
        title={t("communication.smsUpdatesTitle")}
        description={
          maskedPhone
            ? t("communication.smsUpdatesDescWithPhone", { phone: maskedPhone })
            : t("communication.smsUpdatesDesc")
        }
      />

      {/* Email updates — optional */}
      <ToggleRow
        id="comm-email-updates"
        checked={emailUpdates}
        onChange={setEmailUpdates}
        title={t("communication.emailUpdatesTitle")}
        description={t("communication.emailUpdatesDesc")}
      />

      <p className="text-xs text-garnet/70">{t("communication.stopHint")}</p>

      <div>
        <Button
          variant="primary"
          className="w-[220px]"
          onClick={handleSave}
          disabled={updatePrefs.isPending || prefsQuery.isLoading}
        >
          {updatePrefs.isPending ? t("communication.saving") : t("communication.save")}
        </Button>
      </div>
    </div>
  );
}

function ToggleRow({
  id,
  checked,
  onChange,
  title,
  description,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  description: string;
}) {
  return (
    <label
      htmlFor={id}
      className="flex flex-row gap-3 items-start p-4 rounded-lg ring-1 ring-garnet/30 bg-white cursor-pointer hover:bg-garnet/[0.02] transition-colors"
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(Boolean(v))}
        className="size-5 mt-0.5 shrink-0 border-2 border-garnet data-[state=checked]:bg-garnet data-[state=checked]:text-white"
      />
      <div className="flex-1">
        <p className="body-small font-semibold text-garnet">{title}</p>
        <p className="text-xs text-garnet/70 mt-1">{description}</p>
      </div>
    </label>
  );
}
